/**
 * readingTheme — monta o `ThemeSeed` a partir da paleta extraída da capa da
 * leitura atual, e compõe o gate de contraste que `deriveThemeTokens` NÃO faz
 * sozinho (achado de 13/09 — ver `docs/systems/reading-theme.md` §1: o
 * roadmap original afirmava que a validação AA já era parte do contrato da
 * função, e não é — `gradeTokens` é separada, e ninguém a chamava por dentro).
 *
 * F2 do Roadmap PRO — o carro-chefe. Decisão do fundador (13/09,
 * `docs/systems/reading-theme.md` §4): quando o gate reprova, RECUAR e tentar
 * de novo até um teto; se persistir, avisar — nunca forçar um tema ilegível
 * nem emprestar um tema de prateleira do catálogo estático.
 *
 * ## v2 (BUG-237, 13/09) — a capa entra pelo ACCENT, nunca pelo PAPEL
 *
 * A v1 punha duas cores DA CAPA como fundo e como texto (o extremo mais
 * dominante virava `surfaceBase`, o oposto virava `textPrimary`). Medido em
 * 11 capas reais (`docs/systems/reading-theme.md` §10): 5 reprovavam no gate
 * mesmo depois do recuo — entre elas as DUAS que o fundador testou em
 * produção — e as que passavam saíam com página rosa, pêssego ou azul-bebê.
 * A razão é estrutural, não de calibração: cor dominante de capa tem
 * luminância média (0,07–0,58 na amostra), e texto de capa sobre fundo de
 * capa não alcança 4,5:1. O recuo empurrava texto e accent, mas o problema
 * estava no FUNDO, que ele não tocava.
 *
 * A régua da casa, medida nos 28 temas do catálogo (`theme.css`): NENHUM
 * papel tem luminância entre 0,02 e 0,79. Papel escuro é quase-preto tingido
 * (L ≤ 0,015); papel claro é quase-branco quase neutro (L ≥ 0,79, saturação
 * mediana 0,04). A cor do tema mora no accent, na borda e num TINGIDO leve do
 * papel — que é exatamente o que o Share Studio já faz com a capa
 * (`washPaper`, `BOOK_TINT_RATIO = 0.12`). A v2 reusa essa decisão em vez de
 * inventar uma segunda.
 */

import { BOOK_TINT_RATIO, mixHex } from "../features/share-studio/utils/bookPalette";
import { luminance, parseColor, type Rgba } from "./themeContrast";
import { gradeTokens } from "./themeContrast";
import { deriveThemeTokens, type ThemeSeed } from "./themeDerivation";

/** Saturação RELATIVA — mesma fórmula de `bookPalette.ts#isUsable`, não uma segunda regra. */
function relativeSaturation([r, g, b]: Rgba): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max === 0 ? 0 : (max - min) / max;
}

/**
 * Âncoras de papel e tinta — os extremos que o catálogo usa, não valores
 * inventados: Linho (`:root`, o tema padrão) para o claro e Midnight para o
 * escuro. O card (`surfaceRaised`) segue a mesma relação dos dois temas:
 * branco puro sobre o papel claro, um degrau acima do papel no escuro.
 */
const PAPER_LIGHT = "#f4f1ec";
const CARD_LIGHT = "#ffffff";
const INK_LIGHT = "#2a2521";
const PAPER_DARK = "#0a090e";
const CARD_DARK = "#14121f";
const INK_DARK = "#f2eaf0";

/**
 * Quanto da cor dominante entra no papel e na tinta. O papel reusa
 * `BOOK_TINT_RATIO` (a lavagem do Studio). A tinta leva menos: ela é o lado
 * do par de contraste que o recuo empurra, e tingi-la mais do que isto só
 * gasta tentativas do gate.
 */
export const READING_THEME_PAPER_WASH = BOOK_TINT_RATIO;
export const READING_THEME_INK_WASH = 0.08;
/** O card tingido bem menos que o papel: é o que separa card de página. */
const CARD_WASH = 0.04;

/**
 * Capa predominantemente escura → tema escuro. A média é da CAPA INTEIRA
 * (antes do filtro `isUsable`, que joga fora quase-preto e quase-branco), por
 * isso vem de fora como opção: a paleta sozinha não sabe se a capa é escura,
 * porque o preto nunca chega nela. Sem a média, cai no critério que
 * `deriveThemeTokens` já usa para a cor dominante (`isDarkSeed`, 0,18).
 */
export const DARK_COVER_MEAN_LUMINANCE = 0.25;

/**
 * A lavagem carrega MATIZ, não luminância. Medido na 1ª calibração da v2:
 * lavar o papel claro com uma dominante escura (`#282834`, `#57837c`) a 12%
 * derrubava o papel para L 0,70–0,76 — abaixo do piso 0,79 do catálogo —
 * porque a mistura puxa a luminância junto com a cor. Antes de lavar, a
 * dominante é levada para a faixa do papel (pastel no claro, profunda no
 * escuro): mesma matiz, luminância compatível. Alvos = a faixa que o
 * catálogo ocupa (claros L ≥ 0,79; escuros L ≤ 0,015), com folga.
 */
const WASH_TARGET_LUMINANCE_LIGHT = 0.7;
const WASH_TARGET_LUMINANCE_DARK = 0.05;
const WASH_TONE_STEP = 0.1;
const WASH_TONE_MAX_STEPS = 12;

/** Mistura `hex` rumo a `extremo` até a luminância cruzar `alvo` (ou esgotar os passos). */
function toneToward(hex: string, extremo: "#ffffff" | "#000000", alvo: number): string {
  let current = hex;
  for (let i = 0; i < WASH_TONE_MAX_STEPS; i++) {
    const rgba = parseColor(current);
    if (!rgba) return hex;
    const l = luminance(rgba);
    if (extremo === "#ffffff" ? l >= alvo : l <= alvo) return current;
    current = mixHex(current, extremo, WASH_TONE_STEP);
  }
  return current;
}

export interface BuildThemeSeedOptions {
  /** Luminância média de TODOS os pixels da capa (0–1), se conhecida. */
  meanLuminance?: number;
}

/**
 * Monta o `ThemeSeed` (6 papéis) a partir de uma paleta de N cores dominantes
 * (`pickDominantPaletteFromPixels`/`extractDominantPalette`), em ordem de
 * dominância. Ver `docs/systems/reading-theme.md` §2.2 e §10.
 *
 * - `surfaceBase`/`surfaceRaised`/`textPrimary`: as âncoras do catálogo,
 *   LAVADAS com a cor mais dominante da capa — é o que faz o tema "ser" do
 *   livro sem virar a capa inteira como página.
 * - `accent`: a cor de MAIOR saturação da paleta (a que carrega a capa —
 *   normalmente o elemento gráfico, não o fundo). `accentSecondary`, a 2ª.
 * - `borderDefault`: meio-termo entre papel e tinta, como nos 26 temas
 *   estáticos por inspeção.
 *
 * `null` quando a paleta não tem NENHUMA cor com identidade — capa toda
 * preta/branca/cinza cai aqui antes de gastar um ciclo de derivação+gate.
 * (Na v1 o piso era 2 cores porque fundo e texto vinham da capa; agora o
 * papel vem da âncora e uma cor basta para accent.)
 */
export function buildThemeSeedFromPalette(
  paletteHex: readonly string[],
  options: BuildThemeSeedOptions = {},
): ThemeSeed | null {
  const parsed = paletteHex
    .map((hex, index) => ({ hex, rgba: parseColor(hex), index }))
    .filter((c): c is { hex: string; rgba: Rgba; index: number } => c.rgba !== null);
  if (parsed.length < 1) return null;

  // Índice menor = balde mais populoso (`pickDominantPaletteFromPixels` já
  // ordena assim): a dominante é a primeira cor válida, não a mais clara ou
  // mais escura.
  const dominant = parsed[0];

  const mean = options.meanLuminance;
  const dark =
    typeof mean === "number" && Number.isFinite(mean)
      ? mean < DARK_COVER_MEAN_LUMINANCE
      : luminance(dominant.rgba) < 0.18;

  // A tinta da lavagem: a dominante com a luminância levada para a faixa do
  // papel (claro → pastel, escuro → profunda). A TINTA do texto lava com a
  // dominante crua, porque o alvo dela é o extremo oposto ao papel.
  const paperWash = dark
    ? toneToward(dominant.hex, "#000000", WASH_TARGET_LUMINANCE_DARK)
    : toneToward(dominant.hex, "#ffffff", WASH_TARGET_LUMINANCE_LIGHT);
  const surfaceBase = mixHex(dark ? PAPER_DARK : PAPER_LIGHT, paperWash, READING_THEME_PAPER_WASH);
  const surfaceRaised = mixHex(dark ? CARD_DARK : CARD_LIGHT, paperWash, CARD_WASH);
  const textPrimary = mixHex(dark ? INK_DARK : INK_LIGHT, dominant.hex, READING_THEME_INK_WASH);

  const bySaturation = [...parsed].sort(
    (a, b) => relativeSaturation(b.rgba) - relativeSaturation(a.rgba),
  );
  const accent = bySaturation[0];
  const accentSecondary = bySaturation[1] ?? accent;

  return {
    surfaceBase,
    surfaceRaised,
    textPrimary,
    accent: accent.hex,
    accentSecondary: accentSecondary.hex,
    borderDefault: mixHex(surfaceBase, textPrimary, 0.24),
  };
}

/**
 * Política de recuo (decisão A-com-teto, `reading-theme.md` §4).
 *
 * Números calibrados empiricamente contra a amostra de 11 capas (§10), não
 * escolhidos a priori. Com o papel ancorado (v2), o que o gate reprova é
 * quase sempre `accent-on-base` (accent pastel sobre papel claro, ou accent
 * escuro sobre papel escuro): cada tentativa empurra accent E texto em
 * direção ao extremo OPOSTO ao papel.
 */
const MAX_CONTRAST_RETRIES = 4;
const NUDGE_STEP = 0.14;

/** Empurra `hex` em direção ao extremo oposto de `awayFromLuminance`. */
function nudgeAwayFrom(hex: string, awayFromLuminance: number, step: number): string {
  const extremo = awayFromLuminance > 0.5 ? "#000000" : "#ffffff";
  return mixHex(hex, extremo, step);
}

export type ReadingThemeStatus = "ok" | "recusada";

export interface ReadingThemeResult {
  status: ReadingThemeStatus;
  tokens: Record<string, string> | null;
  /** Quantas tentativas (1 = passou de primeira; MAX+1 = desistiu). */
  attempts: number;
}

/**
 * Deriva os tokens do seed e passa pelo gate; se reprovar, recua e tenta de
 * novo até `MAX_CONTRAST_RETRIES`. Nunca devolve tokens que reprovaram.
 */
export function resolveReadingTheme(seed: ThemeSeed): ReadingThemeResult {
  const baseRgba = parseColor(seed.surfaceBase) ?? [255, 255, 255, 1];
  const baseLuminance = luminance(baseRgba);

  let current = seed;
  for (let attempt = 1; attempt <= MAX_CONTRAST_RETRIES + 1; attempt++) {
    const tokens = deriveThemeTokens(current);
    // Cor de seed inválida não é falta de contraste: não há o que recuar.
    if (!tokens) return { status: "recusada", tokens: null, attempts: attempt };

    if (gradeTokens(tokens).passes) return { status: "ok", tokens, attempts: attempt };
    if (attempt > MAX_CONTRAST_RETRIES) return { status: "recusada", tokens: null, attempts: attempt };

    current = {
      ...current,
      textPrimary: nudgeAwayFrom(current.textPrimary, baseLuminance, NUDGE_STEP),
      accent: nudgeAwayFrom(current.accent, baseLuminance, NUDGE_STEP),
    };
  }
  return { status: "recusada", tokens: null, attempts: MAX_CONTRAST_RETRIES + 1 };
}

// ═══════════════════════════════════════════════════════════════════════════
// v3 (27/09/2026) — a cor da capa no PAPEL, dentro da faixa do catálogo.
// Pedido do fundador ("Não é amor" com fundo rosa e botões azuis; Quarta Asa
// como no preview escuro), aprovado com o seletor Claro · Escuro.
// Blueprint: `docs/systems/reading-theme-auto.md`.
//
// ⚠️ NÃO é a v1 de volta. A v1 punha a cor da capa CRUA no papel, que caía na
// luminância média (0,02–0,79 — faixa que nenhum tema do catálogo usa) e
// reprovava o gate em 5 de 11 capas (BUG-237). A v3 leva a cor para DENTRO da
// faixa: no claro, clareada até L ≥ 0,80 (a faixa do Sakura, `#ffe4ee`); no
// escuro, quase-preto tingido (L ≈ 0,012). Medido num protótipo sobre as MESMAS
// funções (32×32 no Chromium, 10 capas reais): 10/10 passam no gate nos dois
// modos (fixture em `tests/fixtures/readingThemeV3Covers.json`).
// ═══════════════════════════════════════════════════════════════════════════

/** Versão do motor gravada com o tema (`currentReadingTheme.engineVersion`). */
export const READING_THEME_ENGINE_VERSION = 3;

export type ReadingThemeMode = "light" | "dark";
/** Preferência da pessoa (`users.readingThemeModePref`); `auto` decide pela capa. */
export type ReadingThemeModePref = "auto" | ReadingThemeMode;

/** Papel claro da v3: a faixa dos claros do catálogo (mín. 0,792 medido). */
export const V3_PAPER_LUMINANCE_LIGHT = 0.8;
/** Papel escuro da v3: quase-preto tingido (máx. 0,015 medido no catálogo). */
export const V3_PAPER_LUMINANCE_DARK = 0.012;
/** Cor "com identidade" o bastante para virar papel. */
const V3_PAPER_MIN_SATURATION = 0.12;

/** `auto` = a régua de hoje (capa escura na média → tema escuro). */
export function resolveReadingThemeMode(
  pref: ReadingThemeModePref | null | undefined,
  meanLuminance: number | null | undefined,
): ReadingThemeMode {
  if (pref === "light" || pref === "dark") return pref;
  return typeof meanLuminance === "number" && Number.isFinite(meanLuminance) && meanLuminance < DARK_COVER_MEAN_LUMINANCE
    ? "dark"
    : "light";
}

/** Mistura `hex` rumo a `extremo` em passos de `step` até cruzar `alvo`. */
function toneTowardV3(hex: string, extremo: "#ffffff" | "#000000", alvo: number, step: number): string {
  let current = hex;
  for (let i = 0; i < 80; i++) {
    const rgba = parseColor(current);
    if (!rgba) return hex;
    const l = luminance(rgba);
    if (extremo === "#ffffff" ? l >= alvo : l <= alvo) return current;
    current = mixHex(current, extremo, step);
  }
  return current;
}

/**
 * O `ThemeSeed` da v3. O PAPEL nasce da cor da capa (a mais dominante com
 * alguma saturação), levada para a faixa do catálogo; o ACCENT é a cor mais
 * saturada que NÃO seja a do papel (senão o botão some no próprio papel). O
 * gate e o recuo são os mesmos de sempre (`resolveReadingTheme`).
 *
 * `null` com paleta vazia (capa sem cor legível) — o chamador trata como hoje.
 */
export function buildThemeSeedFromPaletteV3(
  paletteHex: readonly string[],
  mode: ReadingThemeMode,
): ThemeSeed | null {
  const parsed = paletteHex
    .map((hex) => ({ hex, rgba: parseColor(hex) }))
    .filter((c): c is { hex: string; rgba: Rgba } => c.rgba !== null);
  if (parsed.length === 0) return null;

  const source = (parsed.find((c) => relativeSaturation(c.rgba) >= V3_PAPER_MIN_SATURATION) ?? parsed[0]).hex;
  const bySaturation = [...parsed].sort((a, b) => relativeSaturation(b.rgba) - relativeSaturation(a.rgba));
  const accent = (bySaturation.find((c) => c.hex !== source) ?? bySaturation[0]).hex;
  const accentSecondary = (bySaturation.find((c) => c.hex !== source && c.hex !== accent) ?? bySaturation[0]).hex;

  if (mode === "light") {
    const paper = toneTowardV3(source, "#ffffff", V3_PAPER_LUMINANCE_LIGHT, 0.04);
    const ink = mixHex(INK_LIGHT, source, 0.1);
    return {
      surfaceBase: paper,
      surfaceRaised: mixHex("#ffffff", paper, 0.3),
      textPrimary: ink,
      accent,
      accentSecondary,
      borderDefault: mixHex(paper, ink, 0.2),
    };
  }
  const paper = toneTowardV3(source, "#000000", V3_PAPER_LUMINANCE_DARK, 0.06);
  const ink = mixHex(INK_DARK, source, 0.06);
  return {
    surfaceBase: paper,
    surfaceRaised: mixHex(paper, "#ffffff", 0.06),
    textPrimary: ink,
    accent,
    accentSecondary,
    borderDefault: mixHex(paper, ink, 0.22),
  };
}
